import { addDays, differenceInCalendarDays, format } from 'date-fns';
import es from 'date-fns/locale/es';
import React, { createContext, useContext } from 'react';

import {
  getSpecificFiltersLabel, MAX_DATE_RANGE_DAYS, MAX_HISTORY_YEARS,
} from '@/utils/dashboardDateRange';

interface SearchScopeInfo {
  startDate: Date | null;
  endDate: Date | null;
  // Sin límite de días (hay un buscador específico): se puede consultar todo el histórico
  limited: boolean;
  // Buscadores que levantan el límite, según los que ve el usuario en el dashboard
  specificFiltersLabel: string;
}

// Se pasa por contexto para que el contenedor sea un componente estable y el
// calendario no se remonte (perdiendo el mes visible) en cada render
export const SearchScopeContext = createContext<SearchScopeInfo>({
  startDate: null,
  endDate: null,
  limited: true,
  specificFiltersLabel: getSpecificFiltersLabel(),
});

const HISTORY_DAYS = MAX_HISTORY_YEARS * 365;

const formatDay = (date: Date) => format(date, 'd MMM yyyy', { locale: es });

const CalendarIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

const FilterIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h16l-6 7v6l-4 2v-8z" />
  </svg>
);

interface ScopeRuleProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  active: boolean;
}

const ScopeRule = ({
  icon, title, description, active,
}: ScopeRuleProps) => (
  <li className={`search-scope-rule ${active ? 'search-scope-rule--active' : ''}`}>
    {icon}
    <div className="search-scope-rule__text">
      <span className="search-scope-rule__title">{title}</span>
      <span>{description}</span>
      {active && <span className="search-scope-sr-only">Se aplica a esta búsqueda</span>}
    </div>
  </li>
);

const ScopeSummary = ({
  startDate, endDate, limited,
}: Omit<SearchScopeInfo, 'specificFiltersLabel'>) => {
  if (!startDate) {
    return <span>Selecciona la fecha de inicio</span>;
  }

  if (!endDate) {
    const today = new Date();
    const rangeLimit = addDays(startDate, MAX_DATE_RANGE_DAYS);
    const lastDay = limited && rangeLimit < today ? rangeLimit : today;
    return (
      <span>
        {`${formatDay(startDate)} – … · Elige la fecha de fin, como máximo el ${formatDay(lastDay)}`}
      </span>
    );
  }

  const days = differenceInCalendarDays(endDate, startDate);
  const atLimit = limited && days >= MAX_DATE_RANGE_DAYS;
  const progress = limited
    ? Math.min(100, (days / MAX_DATE_RANGE_DAYS) * 100)
    : Math.max(2, Math.min(100, (days / HISTORY_DAYS) * 100));

  let limitMessage = '';
  if (limited && days > MAX_DATE_RANGE_DAYS) {
    limitMessage = `Supera el máximo de ${MAX_DATE_RANGE_DAYS} días`;
  } else if (atLimit) {
    limitMessage = `Has llegado al máximo de ${MAX_DATE_RANGE_DAYS} días`;
  }

  return (
    <>
      <span>
        {`${formatDay(startDate)} – ${formatDay(endDate)} · `}
        <strong className={atLimit ? 'search-scope-summary__count--limit' : ''}>
          {limited
            ? `${days} de ${MAX_DATE_RANGE_DAYS} días`
            : `${days} días de ${MAX_HISTORY_YEARS} años`}
        </strong>
      </span>
      <div className="search-scope-summary__bar" aria-hidden="true">
        <div
          className={`search-scope-summary__bar-fill ${atLimit ? 'search-scope-summary__bar-fill--limit' : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      {limitMessage && <span className="search-scope-summary__limit">{limitMessage}</span>}
    </>
  );
};

interface SearchScopeCalendarProps {
  className?: string;
  children?: React.ReactNode;
}

// Sustituye al CalendarContainer de react-datepicker manteniendo su rol de diálogo
const SearchScopeCalendar = ({ className, children }: SearchScopeCalendarProps) => {
  const scope = useContext(SearchScopeContext);

  return (
    <div
      className={`${className ?? ''} search-scope-calendar`}
      role="dialog"
      aria-label="Selecciona el rango de fechas"
      aria-modal="true"
    >
      <ul className="search-scope-rules" aria-label="Límites de búsqueda">
        <ScopeRule
          icon={<CalendarIcon />}
          title={`Máximo ${MAX_DATE_RANGE_DAYS} días`}
          description="por búsqueda"
          active={scope.limited}
        />
        <ScopeRule
          icon={<FilterIcon />}
          title={`Hasta ${MAX_HISTORY_YEARS} años`}
          description={`con ${scope.specificFiltersLabel}`}
          active={!scope.limited}
        />
      </ul>
      <div className="search-scope-months">{children}</div>
      <div className="search-scope-summary" aria-live="polite">
        <ScopeSummary
          startDate={scope.startDate}
          endDate={scope.endDate}
          limited={scope.limited}
        />
      </div>
    </div>
  );
};

export default SearchScopeCalendar;
